import React, { useState, useEffect } from "react";
import { useGetFetchNew } from "../../data/Queries";
import { Container, Nav, Tab } from "react-bootstrap";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import Modal from "react-modal";
import { EditIcon, DeleteIcon } from "../../images/icons/website-icons";
import ManageTeamMember from "./ManageTeamMember";
import ManageTeamDepartment from "./ManageTeamDepartment";
import { formatDate } from "../../utils/utilityfunc";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../components-react/ReactAlert";
import {
  delete_team_member,
  get_team_departments,
  get_team_members,
} from "../../data/ApiEndpoints";
import FilterContainer from "../common-components/filters/filter-container";
import SearchFilter from "../common-components/filters/search-filter";
import SearchFilterWithButton from "../common-components/filters/search-filter-with-button";

// Custom styles for department headers
const styles = {
  pageHeader: {
    marginBottom: "30px",
    borderBottom: "1px solid #e9ecef",
    paddingBottom: "15px",
  },
  customHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  mainTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#343a40",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
  },
  primaryButton: {
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "4px",
    cursor: "pointer",
  },
  secondaryButton: {
    backgroundColor: "#fff",
    color: "#343a40",
    border: "1px solid #dc3545",
    padding: "8px 16px",
    borderRadius: "4px",
    cursor: "pointer",
  },
  tabContainer: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
    marginBottom: "30px",
    width: "100%",
  },
  tabNav: {
    borderBottom: "1px solid #dee2e6",
    padding: "0 20px",
  },
  tabNavItem: {
    margin: "0 5px",
  },
  tabNavLink: {
    color: "#495057",
    border: "none",
    borderBottom: "3px solid transparent",
    borderRadius: "0",
    padding: "15px 20px",
    fontWeight: "500",
    transition: "all 0.2s ease",
    textTransform: "capitalize",
  },
  tabNavLinkActive: {
    color: "#dc3545",
    borderBottomColor: "#dc3545",
    background: "transparent",
  },
  tabContent: {
    padding: "25px",
    width: "100%",
  },
  tabPane: {
    width: "100%",
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "25px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
    width: "100%",
  },
  emptyStateText: {
    fontSize: "16px",
    color: "#6c757d",
    textAlign: "center",
  },
};

const TeamLanding = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(8);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [modValues, setModValues] = useState({
    isModOpen: false,
    cardName: "",
  });
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");

  // Make sure we pass the token to API calls
  useEffect(() => {
    console.log(
      "User info data:",
      userInfoData?.token ? "Token available" : "No token"
    );
  }, [userInfoData]);

  const { data, isLoading, refetch } = useGetFetchNew({
    token: userInfoData?.token,
    key: "team-members",
    url: get_team_members,
    filters: {
      limit: pageLimit,
      page: page + 1,
      search: searchQuery,
    },
  });

  console.log("Team data:", data);

  // Get team departments
  const { data: departmentsData, refetch: refetchDepartments } = useGetFetchNew(
    {
      token: userInfoData?.token,
      key: "team-departments",
      url: get_team_departments,
    }
  );

  // Set the active tab to the first department when data loads
  useEffect(() => {
    if (data?.data && data.data.length > 0 && !activeTab) {
      setActiveTab(data.data[0].uid || `tab-${0}`);
    }
  }, [data, activeTab]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModValues({
      isModOpen: false,
      cardName: "",
    });
  };

  const handleDepartmentModalClose = () => {
    setIsDepartmentModalOpen(false);
  };

  const handleAddTeamMember = () => {
    setInitialValues(null);
    setIsModalOpen(true);
    setModValues({
      isModOpen: true,
      cardName: "add",
    });
  };

  const handleAddDepartment = () => {
    setIsDepartmentModalOpen(true);
  };

  const handleEdit = (row) => {
    console.log("Editing team member:", row);

    // Create a copy of row data to avoid reference issues
    const rowData = { ...row };

    // Ensure UID is present and valid
    if (!rowData.uid) {
      console.error("Warning: Team member data is missing UID!", rowData);
      toast.error("Cannot edit team member: Missing identifier");
      return;
    }

    console.log("Prepared team data for edit:", rowData);
    setInitialValues(rowData);
    setIsModalOpen(true);
    setModValues({
      isModOpen: true,
      cardName: "edit",
    });
  };

  const handleDelete = async (uid) => {
    const handleConfirm = async () => {
      setLoading(true);
      try {
        console.log("Deleting team member with UID:", uid);
        const result = await dispatch(
          commonPostPutApi({
            method: "DELETE",
            endpoint: `${delete_team_member}/${uid}`,
            token: userInfoData?.token,
          })
        );

        console.log("Delete response:", result);

        if (result?.payload?.status) {
          toast.success(
            result?.payload?.message || "Team member deleted successfully!"
          );
          refetch();
        } else {
          const errorMessage =
            result?.error?.message || result?.payload?.message;
          toast.error(errorMessage || "Failed to delete team member!");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Something went wrong!");
      } finally {
        setLoading(false);
      }
    };

    showConfirmAlert(
      handleConfirm,
      "Are you sure you want to delete this team member?"
    );
  };

  const tbColumns = [
    {
      dataField: "name",
      text: "Name",
      formatter: (cell, row) => <span className="tb-data">{row?.name}</span>,
    },
    {
      dataField: "email",
      text: "Email",
      formatter: (cell, row) => (
        <span className="tb-data" style={{ textTransform: "none" }}>
          {row?.email}
        </span>
      ),
    },
    {
      dataField: "profile ",
      text: "Profile Image",
      formatter: (cell, row) => (
        <span className="tb-data">
          <img
            src={row?.profile}
            alt="Profile"
            className="img-fluid"
            style={{ width: "50px", height: "50px" }}
          />
        </span>
      ),
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (cell, row) => (
        <span className="tb-data">{formatDate(row?.date_added)}</span>
      ),
    },
    {
      dataField: "bio",
      text: "Designation",
      formatter: (cell, row) => (
        <span className="tb-data">{row?.bio || "NA"}</span>
      ),
    },
    {
      dataField: "seq_no",
      text: "Sequence",
      formatter: (cell, row) => (
        <span className="tb-data">{row?.seq_no || "NA"}</span>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => {
        return (
          <div className="actionButtonContainer">
            <button
              className="actionBtn icon edit"
              onClick={() => handleEdit(row)}
              disabled={loading}
            >
              <EditIcon />
            </button>

            <button
              className="actionBtn icon delete"
              onClick={() => handleDelete(row.uid)}
              disabled={loading}
            >
              {loading ? <span>...</span> : <DeleteIcon />}
            </button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    refetch();
  }, [searchQuery]);

  const handleReset = () => {
    setSearchQuery("");
  };

  return (
    <Container className="bg-cloud-k px-50px gobal-container" fluid>
      <div style={styles.pageHeader}>
        <div style={styles.customHeader}>
          <h1 style={styles.mainTitle}>Team Members</h1>
          <div style={styles.buttonGroup}>
            <button
              className="btn btn-danger"
              onClick={handleAddTeamMember}
              disabled={isLoading}
              style={styles.primaryButton}
            >
              + Add Team Member
            </button>
            <button
              className="btn"
              onClick={handleAddDepartment}
              style={styles.secondaryButton}
            >
              Manage Departments
            </button>
          </div>
        </div>
      </div>

      <FilterContainer onReset={handleReset} grid={2}>
        <SearchFilterWithButton
          selectedSearch={searchQuery}
          setSelectedSearch={(value) => setSearchQuery(value)}
          onSearch={(value) => {
            setSearchQuery(value);
          }}
        />
      </FilterContainer>

      <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt30">
        {data?.data && data.data.length > 0 ? (
          <div style={styles.tabContainer}>
            <Tab.Container
              id="department-tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
            >
              <Nav style={styles.tabNav} variant="tabs">
                {data.data.map((department, index) => (
                  <Nav.Item
                    key={department.uid || `tab-${index}`}
                    style={styles.tabNavItem}
                  >
                    <Nav.Link
                      eventKey={department.uid || `tab-${index}`}
                      style={
                        activeTab === (department.uid || `tab-${index}`)
                          ? { ...styles.tabNavLink, ...styles.tabNavLinkActive }
                          : styles.tabNavLink
                      }
                    >
                      {department.title}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              <Tab.Content style={styles.tabContent}>
                {data.data.map((department, index) => (
                  <Tab.Pane
                    key={department.uid || `tab-${index}`}
                    eventKey={department.uid || `tab-${index}`}
                    style={styles.tabPane}
                  >
                    <PaymentLinkTable
                      tableData={department?.members}
                      tableColumns={tbColumns}
                      userInfoData={userInfoData}
                      isPage={page}
                      setIsPage={(value) => setPage(value)}
                      isLoading={isLoading}
                      tablePagination={data?.pagination}
                      setPageLimit={setPageLimit}
                      pageLimit={pageLimit}
                    />
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </Tab.Container>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <p style={styles.emptyStateText}>
              No team members found. Click "Add Team Member" to get started.
            </p>
          </div>
        )}
      </div>

      {/* Team Member Modal */}
      <Modal
        isOpen={isModalOpen}
        onHide={handleModalClose}
        ariaHideApp={false}
        contentLabel="Team Member Modal"
        onRequestClose={handleModalClose}
      >
        {isModalOpen && (
          <ManageTeamMember
            isEdit={modValues.cardName === "edit"}
            closeModal={handleModalClose}
            token={userInfoData?.token}
            getTeamData={refetch}
            initialValues={initialValues}
            departments={departmentsData?.data || []}
          />
        )}
      </Modal>

      {/* Department Modal */}
      <Modal
        isOpen={isDepartmentModalOpen}
        onHide={handleDepartmentModalClose}
        ariaHideApp={false}
        contentLabel="Department Modal"
        onRequestClose={handleDepartmentModalClose}
      >
        {isDepartmentModalOpen && (
          <ManageTeamDepartment
            closeModal={handleDepartmentModalClose}
            token={userInfoData?.token}
            getDepartmentData={refetchDepartments}
          />
        )}
      </Modal>
    </Container>
  );
};

export default TeamLanding;
